import { useRef, useEffect } from 'react'
import * as echarts from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

echarts.use([
  BarChart,
  LineChart,
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  CanvasRenderer,
])

// One ECharts canvas, as StackedDayChart keeps one: initialised only once
// there is data, resized with its box, disposed on unmount, and the empty
// state a sibling rather than the other branch of a ternary (ECharts owns the
// container's children, so React must never reconcile anything into it).
const EChart = ({ option, height = 220, isEmpty, emptyText }) => {
  const containerRef = useRef(null)
  const chartRef = useRef(null)

  useEffect(() => {
    if (isEmpty || !containerRef.current) return undefined
    const chart = echarts.init(containerRef.current)
    chartRef.current = chart
    const observer = new ResizeObserver(() => chart.resize())
    observer.observe(containerRef.current)
    return () => {
      observer.disconnect()
      chart.dispose()
      chartRef.current = null
    }
  }, [isEmpty])

  useEffect(() => {
    if (chartRef.current && option)
      chartRef.current.setOption(option, { replaceMerge: ['series'] })
  }, [option, isEmpty])

  return (
    <Box sx={{ position: 'relative', width: '100%', height }}>
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height,
          visibility: isEmpty ? 'hidden' : 'visible',
        }}
      />
      {isEmpty && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default EChart
